import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { PageHeader } from '../../components/layout/PageHeader';
import { Combobox } from '../../components/ui/Combobox';
import { presetToRange, toLocalYmd, type DateRange } from '../../lib/reportingDateRange';
import type { ReportingDatePreset } from '../../types/reporting';
import {
  demographicsFreshnessMessage,
  resolveDemographicsFreshnessNotice,
} from '../../lib/reporting/demographicsFreshness';
import { DateRangePicker } from './DateRangePicker';
import { DemographicsPanel } from './DemographicsPanel';
import {
  getReportingDemographics,
  getReportingDemographicsFreshness,
  getReportingFilterOptions,
} from './api';

// Audience — the STAFF surface over the Meta demographics warehouse, and the
// first read of it anywhere.
//
// ⚠️ ONE ACCOUNT AT A TIME, and that is the data model rather than a simplified
// v1. Each account has its own set of stored pull windows, so "the window that
// answers this range" can differ per account; summing two accounts answered by
// two different windows would mix grains inside one figure, which is exactly
// what the read core refuses to do. A group scope (the portal's case) needs an
// explicit rule for that disagreement and does not have one yet.
//
// ⚠️ STAFF FIRST, PORTAL LATER — deliberately. It validates the cuts before a
// client sees them, and it needs no PortalScope work. When the portal picks this
// up it gets a scope-taking query of its own, never this endpoint with a filter
// added (CLAUDE.md rule 21).

/** dd MMM — the same short form the panel's own sentences use. */
function formatFreshnessDate(iso: string): string {
  const d = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', timeZone: 'UTC' });
}

export function AudiencePage(): React.JSX.Element {
  // Deep-linkable, so an AM can send "look at this club's audience" — the same
  // one-time-seed posture as the other reporting pages' ?accountId=.
  const [searchParams, setSearchParams] = useSearchParams();
  const [accountId, setAccountId] = useState(() => searchParams.get('accountId') ?? '');
  const [preset, setPreset] = useState<ReportingDatePreset>('past_30');
  const [custom, setCustom] = useState<DateRange>(() => presetToRange('past_30', new Date()));

  const range = useMemo<DateRange>(() => {
    if (preset !== 'custom') return presetToRange(preset, new Date(), custom);
    return custom;
  }, [preset, custom]);
  const rangeValid = Boolean(range.from && range.to && range.from <= range.to);

  // The account list is the set that owns ATTRIBUTED spend — i.e. exactly the
  // accounts that could have demographics — so it needs no separate endpoint.
  const optionsQuery = useQuery({
    queryKey: ['reporting-filter-options'],
    queryFn: getReportingFilterOptions,
    staleTime: 10 * 60 * 1000,
  });

  const accountOptions = useMemo(() => {
    const facets = optionsQuery.data?.accountFacets ?? [];
    return [...facets]
      .map((f) => ({
        value: f.accountId,
        label: f.accountName ?? f.accountId,
        description: f.brandName ?? undefined,
        // Brand + group so an AM can find a club by the marque it belongs to.
        keywords: [f.brandName ?? '', f.groupName ?? ''].filter(Boolean).join(' '),
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [optionsQuery.data]);

  const demographics = useQuery({
    queryKey: ['reporting-demographics', accountId, range.from, range.to],
    queryFn: () =>
      getReportingDemographics({ accountId, from: range.from, to: range.to }),
    enabled: Boolean(accountId) && rangeValid,
  });

  /**
   * ⚠️⚠️ THE SIGNAL THAT WAS MISSING. On 2026-08-31 the nightly demographics job
   * had been failing on EVERY fire — its scheduler was created without
   * `roles/run.invoker` — and nothing anywhere said so. Roster-wide and
   * account-independent, so it is fetched whether or not an account is chosen:
   * a dead pull is a dead pull before you pick anything.
   *
   * ⚠️ Long staleTime. It moves once a night at most, and re-asking it on every
   * account change would be a BigQuery scan per click for an answer that cannot
   * have changed.
   */
  const freshness = useQuery({
    queryKey: ['reporting-demographics-freshness'],
    queryFn: getReportingDemographicsFreshness,
    staleTime: 30 * 60 * 1000,
  });

  // ⚠️ The VIEWER'S local today, via `toLocalYmd` — the UTC date names yesterday
  // for a Brisbane reader all morning, which would report the pull a day staler
  // than it is every single day before 10:00. Same reasoning as the pull window.
  const freshnessNotice = resolveDemographicsFreshnessNotice({
    today: toLocalYmd(new Date()),
    freshness: freshness.data,
    unavailable: Boolean(freshness.error),
  });

  const onAccountChange = (next: string): void => {
    setAccountId(next);
    // Keep the URL in step so the view is shareable; replace rather than push so
    // the back button leaves the page instead of walking the picker's history.
    const params = new URLSearchParams(searchParams);
    if (next) params.set('accountId', next);
    else params.delete('accountId');
    setSearchParams(params, { replace: true });
  };

  const selectedName = accountOptions.find((o) => o.value === accountId)?.label ?? null;

  return (
    <div>
      <PageHeader
        title="Audience"
        description="Who responds to a client's Meta ads — by age, gender, region, platform and device."
      />

      {/* ⚠️⚠️ IT LEADS THE PAGE, above the picker and above any figure. A caveat
          read after a number has been believed is not a caveat — and this one is
          the difference between "this club had a quiet month" and "the job that
          writes this stopped running three days ago". Amber only when it is
          genuinely behind; a permanently-coloured line is one people stop
          seeing. */}
      {freshnessNotice ? (
        <p
          className={
            freshnessNotice.kind === 'stale' || freshnessNotice.kind === 'never'
              ? 'mb-4 max-w-3xl text-sm text-amber-700'
              : 'mb-4 max-w-3xl text-xs text-slate-500'
          }
          data-testid="demographics-freshness-note"
        >
          {demographicsFreshnessMessage(freshnessNotice, formatFreshnessDate)}
        </p>
      ) : null}

      <div className="mb-6 flex flex-wrap items-end gap-3">
        <div>
          <label
            htmlFor="audience-account"
            className="mb-1 block text-xs font-medium uppercase tracking-wide text-slate-400"
          >
            account
          </label>
          <Combobox
            id="audience-account"
            value={accountId}
            onValueChange={onAccountChange}
            options={accountOptions}
            placeholder={optionsQuery.isLoading ? 'Loading accounts…' : 'Choose an account…'}
            searchPlaceholder="Search accounts…"
            aria-label="Account"
            className="w-72"
          />
        </div>
        <DateRangePicker
          preset={preset}
          onPresetChange={setPreset}
          custom={custom}
          onCustomChange={setCustom}
        />
      </div>

      {!accountId ? (
        // ⚠️ Nothing is shown until an account is picked — a roster-wide audience
        // dump would be the multi-account blend the read core refuses.
        <p className="max-w-3xl text-sm text-slate-500">
          pick an account to see its audience breakdown. these figures come from meta&apos;s
          demographic breakdowns, which are stored per pull window rather than per day — so the
          period they cover is stated on the panel and may be wider than the range above.
        </p>
      ) : !rangeValid ? (
        <p className="max-w-3xl text-sm text-slate-500">pick a date range.</p>
      ) : (
        <>
          {selectedName ? (
            <h2 className="mb-4 text-base font-semibold text-foreground">{selectedName}</h2>
          ) : null}
          <DemographicsPanel
            data={demographics.data}
            isLoading={demographics.isLoading}
            error={demographics.error}
          />
        </>
      )}
    </div>
  );
}
